'use client'

import { IconHeart, IconHeartFill } from '@macarongfactory/web-icons'
import { BottomCTA, TextButton } from '@macarongfactory/web-ui'
import { useRouter } from 'next/navigation'
import { useState } from 'react'

import { formatNumber, isSafeUrl } from '@/shared/lib'
import { getPlatform } from '@/shared/lib/appBridge/appBridge.utils'
import { useIsMounted } from '@/shared/model'

import useToggleMycleContentLikeMutation from '../api/mutation/useToggleMycleContentLikeMutation'
import { carStoryTracking } from '../lib/tracking'

interface ICarStoryContentDetailCtaProps {
  contentId: number
  title: string
  label: string
  url: string
  initialLikeCount: number
  initialLiked: boolean
}

const CarStoryContentDetailCta = ({
  contentId,
  title,
  label,
  url,
  initialLikeCount,
  initialLiked,
}: ICarStoryContentDetailCtaProps) => {
  const router = useRouter()
  const isMounted = useIsMounted()
  const [liked, setLiked] = useState(initialLiked)
  const [likeCount, setLikeCount] = useState(initialLikeCount)
  const { mutate, isPending } = useToggleMycleContentLikeMutation(contentId)

  const handleLikeClick = () => {
    if (isPending) return

    const prevLiked = liked
    const nextLiked = !prevLiked

    if (nextLiked) {
      carStoryTracking.clickDetailLike(title, contentId)
    } else {
      carStoryTracking.clickDetailUnlike(title, contentId)
    }

    setLiked(nextLiked)
    setLikeCount(prev => prev + (nextLiked ? 1 : -1))

    mutate(
      { currentLiked: prevLiked },
      {
        onSuccess: ({ liked: serverLiked }) => {
          if (serverLiked === nextLiked) return
          setLiked(serverLiked)
          setLikeCount(prev => prev + (serverLiked ? 1 : -1))
        },
        onError: () => {
          setLiked(prevLiked)
          setLikeCount(prev => prev - (nextLiked ? 1 : -1))
        },
      }
    )
  }

  const isInApp = isMounted && getPlatform() !== null

  const handleCtaClick = () => {
    if (!isSafeUrl(url, isInApp)) return
    carStoryTracking.clickDetailCta(label, contentId)

    // 차량이야기 내부 경로면 Android WebView가 새 뷰로 푸시하지 않도록 SPA 전환
    try {
      const parsed = new URL(url)
      const isInternalCarStory =
        parsed.origin === window.location.origin &&
        parsed.pathname.includes('/car-story')

      if (isInternalCarStory) {
        const isCarStoryHome = parsed.pathname.endsWith('/car-story')
        if (isCarStoryHome) {
          // 홈↔상세 반복 시 스택 누적을 막기 위해, 홈에서 들어온 경우(뒤에 홈이 있음)엔
          // 뒤로가기로 복귀하고, 딥링크 등 직접 진입(뒤에 홈 없음)일 때만 홈으로 이동
          if (window.history.length > 1) {
            router.back()
            return
          }

          router.replace(`${parsed}`)
          return
        }

        router.push(`${parsed}`)
        return
      }
    } catch {
      // URL 파싱 실패 시 기존 동작으로 fallthrough
    }

    window.location.href = url
  }

  return (
    <div className='fixed bottom-0 left-0 right-0 z-50 mx-auto max-w-150'>
      <BottomCTA.Vertical
        position='relative'
        top={
          <TextButton
            variant='leading-icon'
            leadingIcon={
              liked ? (
                <IconHeartFill width={20} height={20} color='#F84848' />
              ) : (
                <IconHeart width={20} height={20} />
              )
            }
            text={formatNumber(likeCount)}
            typography='title-1-medium'
            textColor='text-foreground-neutral-normal'
            iconColor={
              liked
                ? 'text-foreground-danger-strong'
                : 'text-foreground-neutral-normal'
            }
            clickable={handleLikeClick}
          />
        }
        mainButton={
          <BottomCTA.MainButton.SolidButton
            text={label}
            onClick={handleCtaClick}
          />
        }
      />
    </div>
  )
}

export default CarStoryContentDetailCta
